import React from 'react';
import { FormItemProps } from '../type/form-item';
import { FormContext } from '../form';
import { getObj, isEmpty } from '../util';
import ExclamationCircle from 'icon/exclamation_circle.svg';
import { FormItemInterface } from '../type';
import { useTranslation } from 'react-i18next';

import './style.scss';

const CSS_PREFIX = 'report-form-item';

const FormItemElement: React.FC<FormItemProps> = (props: FormItemProps) => {
  const {
    name,
    rules = [],
    label = '',
    tip = '',
    prefix = '',
    prompt = '',
    renderContainer = true,
    className = '',
    required: itemRequired,
    group,
  } = props;
  const { state, dispatch } = React.useContext(FormContext);
  const inputRef = React.useRef(null);

  const { t } = useTranslation();

  const formItemInterface: FormItemInterface = {
    group,
    focus() {
      if (inputRef.current) {
        // @ts-ignore
        const ele = inputRef.current.querySelector('input, textarea, select, checkbox, radio');
        if (ele) {
          // 因为使用了checkbox组件，在组件中设置了一个假的input以获得inputRef
          // 但是在display:none，情况下是无法进行滚动的，要滚动的话就需要滚动到他的父节点
          if (ele.style.display === 'none') {
            ele.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          } else {
            ele.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            setTimeout(() => {
              ele.focus();
            }, 500);
          }
        } else {
          // @ts-ignore
          inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          // @ts-ignore
          inputRef.current.focus();
        }
      }
    },
    async validate(eventName) {
      const value = getObj(state.values, name);
      const curErrors = getObj(state.errors, name, []);
      let isValid = true;
      if (itemRequired) {
        const hasRequired = rules.some((item) => Boolean(item.required));
        if (!hasRequired) {
          rules.unshift({
            required: true,
            error: t('auOSA_webForm_error_fillOut'),
            isBreak: true,
            trigger: 'blur',
          });
        }
      }
      const errors: string[] = [];
      if (rules.length === 0) {
        isValid = true;
      } else {
        for (let i = 0; i < rules.length; i++) {
          const rule = rules[i];
          const {
            max,
            min,
            required,
            validator,
            regexp,
            trigger = 'submit',
            isBreak = true,
            error: ruleError,
            group: isGroup,
          } = rule;
          let ruleIsValid = true;

          const checkValid = async (noTip = false) => {
            if (isGroup) {
              const groupInputs = getObj(state.groups, group!);
              ruleIsValid = Object.getOwnPropertyNames(groupInputs).some((propertyName) => {
                // group里只要有一个不空即可
                return !isEmpty(getObj(state.values, propertyName));
              });
            } else if (max) {
              ruleIsValid = value.length <= max;
            } else if (min) {
              ruleIsValid = value.length >= min;
            } else if (required) {
              ruleIsValid = !isEmpty(value);
            } else if (validator) {
              ruleIsValid = await validator(value);
            } else if (regexp) {
              ruleIsValid = regexp.test(value);
            }
            if (noTip) {
              return ruleIsValid;
            }
            if (ruleIsValid === false) {
              errors.push(ruleError);
            }
            return ruleIsValid;
          };

          if (curErrors.length > 0) {
            isValid = await checkValid();
            // eslint-disable-next-line @typescript-eslint/brace-style
          }
          // change = func > blur > submit
          else if (eventName === 'change') {
            // eslint-disable-next-line max-depth
            if (trigger === 'change') {
              isValid = await checkValid();
            } else if (typeof trigger === 'function' && trigger(value)) {
              isValid = await checkValid();
            }
          } else if (eventName === 'blur') {
            if (trigger === 'blur') {
              isValid = await checkValid();
            }
          } else if (eventName === 'isEdit') {
            isValid = await checkValid(true);
          } else {
            isValid = await checkValid();
          }

          if (isValid === false && isBreak) {
            // 不执行剩下的规则
            break;
          }
        }
        // 特殊情况，在编辑时期只判断是否符合要求，不提示错误
        if (eventName === 'isEdit') {
          return Promise.resolve(isValid);
        }
        // 等待下一个microTask，避免绘制时冲突
        await Promise.resolve();
        dispatch({ type: 'error', name, payload: errors });
      }
      return Promise.resolve(errors.length === 0);
    },
  };

  const handleOnChange = (e: any) => {
    let value = e;
    if (e.target) {
      // eslint-disable-next-line prefer-destructuring
      value = e.target.value;
    }
    dispatch({ name, type: 'change', payload: value });
    if (group) {
      const groupInputs = getObj(state.groups, group);
      Object.getOwnPropertyNames(groupInputs).forEach((propertyName) => {
        if (propertyName !== name) {
          state.groups[group][propertyName].validate('change');
        }
      });
    }
  };
  const fieldValue = getObj(state.values, name);
  const fieldValueRef = React.useRef(fieldValue);
  const { children } = props;
  const { props: childrenProps } = children;
  const childValue = childrenProps.value;
  const childOptions = childrenProps.options;
  React.useMemo(() => {
    if (fieldValue) {
      formItemInterface.validate('change');
    }
  }, [fieldValue, fieldValue.length]);

  React.useMemo(() => {
    if (!isEmpty(childValue)) {
      dispatch({ type: 'change', name, payload: childValue });
      formItemInterface.validate('change');
      fieldValueRef.current = childValue;
    }
  }, [childValue]);

  const handleOnBlur = () => {
    formItemInterface.validate('blur');
  };
  React.useEffect(() => {
    if (!isEmpty(fieldValue)) {
      formItemInterface.validate('submit');
    }
    dispatch({
      type: 'bindInput',
      payload: formItemInterface,
      name,
    });
    return () => {
      dispatch({ type: 'unmount', name, payload: { group } });
    };
  }, [state.values, state.errors]);
  const fieldErrors = getObj(state.errors, name, []);
  const handles = {
    onChange: handleOnChange,
    onBlur: handleOnBlur,
  };

  const renderChild = () => {
    if (React.isValidElement(children)) {
      // @ts-ignore
      const childProps = { ...children.props };
      // @ts-ignore
      childProps.value = fieldValue;

      ['onChange', 'onBlur'].forEach((eventName) => {
        // @ts-ignore
        childProps[eventName] = (...args: any[]) => {
          // @ts-ignore
          handles[eventName](...args);
          // @ts-ignore
          if (children.props[eventName]) {
            // @ts-ignore
            children.props[eventName](...args);
          }
        };
      });
      return React.cloneElement(children, childProps);
    } else {
      return children;
    }
  };

  const childComponent = React.useMemo(renderChild, [childValue, fieldValue, childOptions, childrenProps]);

  return renderContainer ? (
    <div className={`${CSS_PREFIX} ${className}`}>
      {label && (
        <div className={`${CSS_PREFIX}-label`}>
          {label}
          <span className={`${CSS_PREFIX}-label-required`}>{itemRequired && ' *'}</span>
          {Array.isArray(tip) ? (
            tip.map((tipItem, index) => {
              return (
                <div className={`${CSS_PREFIX}-tip`} key={index}>
                  {tipItem}
                </div>
              );
            })
          ) : (
            <div className={`${CSS_PREFIX}-tip`}>{tip}</div>
          )}
        </div>
      )}
      <div className={`${CSS_PREFIX}-input`} tabIndex={-1} ref={inputRef}>
        {Boolean(prefix) && <div className={`${CSS_PREFIX}-prefix`}>{prefix}</div>}
        {childComponent}
      </div>
      {Boolean(prompt) && <div className={`${CSS_PREFIX}-prompt`}>{prompt}</div>}
      {fieldErrors.length > 0 && (
        <div className={`${CSS_PREFIX}-error`}>
          {fieldErrors.map((error: string) => (
            <div key={error} className={`${CSS_PREFIX}-error-item`}>
              <ExclamationCircle className="exclamation-icon" />
              {error}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : (
    <div tabIndex={-1} ref={inputRef}>
      {' '}
      {childComponent}
    </div>
  );
};

export const FormItem = React.memo(FormItemElement);
